/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { addMonths } from "date-fns"
import Decimal from "decimal.js"
import _ from "lodash"
import { z } from "zod"

// AIDEV-NOTE: Keep validation callback boundaries typed as unknown and narrow inside so strict readonly linting and zod interop stay compatible.
type YearMonthValidation = {
  month: number
  year: number
}

type CashFlowValidation = {
  name: string
  frequency: string
  start: YearMonthValidation
  end?: YearMonthValidation
}

type BaseUserParametersValidation = {
  retirementStart: YearMonthValidation
  retirementHorizonMonths: number
  allocationMode: "static" | "glide path"
  glidePathLengthMonths?: number
  finalAssets?: unknown
  cashFlows: CashFlowValidation[]
}

const preprocessToNumber = (val: unknown) => {
  if (typeof val === "string") {
    const trimmed = val.trim()
    if (trimmed === "") return val
    return Number(trimmed)
  }
  return val
}

const preprocessOptionalToNumber = (val: unknown) => {
  if (typeof val === "string") {
    const trimmed = val.trim()
    // Convert empty string to undefined so the optional check passes
    if (trimmed === "") return undefined
    return Number(trimmed)
  }
  return val
}

const preprocessToDecimal = (val: unknown) => {
  if (typeof val === "string") {
    const trimmed = val.trim()
    if (trimmed === "" || trimmed === "-" || trimmed === "+") {
      return val // Allow intermediate typing states
    }
    try {
      return new Decimal(trimmed)
    } catch {
      return val // Let zod handle the error
    }
  }
  if (typeof val === "number") {
    return new Decimal(val)
  }
  if (val instanceof Decimal) {
    return val
  }
  return val
}

export const validNumber = z.preprocess(
  preprocessToNumber,
  z
    .number({
      invalid_type_error: "Please provide a valid number.",
      required_error: "This field is required.",
    })
    .refine(num => !isNaN(num) && isFinite(num), {
      message: "Please provide a valid finite number.",
    })
)

export const validInteger = validNumber.refine(num => Number.isInteger(num), {
  message: "Please provide a whole number.",
})

export const validPositiveInteger = validInteger.refine(num => num >= 0, {
  message: "Please provide a number that is 0 or greater.",
})

const validPositiveNonZeroInteger = validPositiveInteger.refine(num => num > 0, {
  message: "Please provide a number greater than 0.",
})

const validRetirementHorizonMonths = validPositiveInteger
  .refine(num => num >= 1, {
    message: "Retirement horizon must be at least 1 month.",
  })
  .refine(num => num <= 730, {
    message: "Retirement horizon cannot exceed 730 months.",
  })

export const validOptionalPositiveInteger = z.preprocess(
  preprocessOptionalToNumber,
  validPositiveInteger.optional()
)

const validPositiveFloat = validNumber.refine(num => num >= 0, {
  message: "Please provide a number that is 0 or greater.",
})

const validMonth = validPositiveInteger.refine(month => month >= 1 && month <= 12, {
  message: "Please provide a valid month.",
})

const validFamaFrenchFactorLoading = validNumber.refine(fff => fff >= -100 && fff <= 100, {
  message: "Please provide a number between -100 and 100",
})

// using decimal.js to avoid float imprecision problems
// i.e. sum of asset allocations = 100.0000001 etc
const validAssetAllocation = z.preprocess(
  preprocessToDecimal,
  z
    .instanceof(Decimal, {
      message: "Please provide a valid number.",
    })
    .refine(
      (decimalInput: unknown) => {
        return decimalInput instanceof Decimal && decimalInput.gte(-50) && decimalInput.lte(150)
      },
      {
        message: "Please provide a number between -50 and 150",
      }
    )
)

export const yearMonthSchema = z.object(
  {
    month: validMonth,
    year: validPositiveInteger,
  },
  {
    invalid_type_error: "Please provide a valid month and year.",
  }
)

export const allocationSchema = z
  .object({
    spxTrPct: validAssetAllocation,
    worldExUsEquitiesPct: validAssetAllocation,
    cashPct: validAssetAllocation,
    bm10yPct: validAssetAllocation,
    bm30yPct: validAssetAllocation,
    commoditiesPct: validAssetAllocation,
    goldPct: validAssetAllocation,
  })
  .superRefine((allocationsInput: unknown, refinementContext: unknown) => {
    const ctx = refinementContext as z.RefinementCtx
    if (!allocationsInput || typeof allocationsInput !== "object") {
      return
    }

    let allocationSum = new Decimal(0)
    for (const value of Object.values(allocationsInput as Record<string, unknown>)) {
      if (!(value instanceof Decimal)) {
        return
      }
      allocationSum = allocationSum.plus(value)
    }

    if (!allocationSum.equals(new Decimal(100))) {
      // AIDEV-NOTE: The "root" segment is what puts this error at `allocation.root` in the
      // form, beside any per-field errors. With an empty path, @hookform/resolvers >= 4 sets it
      // at `allocation` itself, and because zod reports the fields' issues first it then
      // replaces them: the field's own message vanishes and Assets highlights every field.
      // Both workbook parsers' formatZodErrors drop this trailing segment from upload messages.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `The sum of the allocation percentages must be 100 (it's currently ${allocationSum.toString()}). (The Fama-French factor loadings don't contribute to this sum.)`,
        path: ["root"],
      })
    }
  })
  .transform((allocationsInput: unknown) => {
    if (!allocationsInput || typeof allocationsInput !== "object") {
      throw new Error("Allocation transform requires object input.")
    }

    const allocationRecord = allocationsInput as Record<string, unknown>
    const spxTrPct = allocationRecord.spxTrPct
    const worldExUsEquitiesPct = allocationRecord.worldExUsEquitiesPct
    const cashPct = allocationRecord.cashPct
    const bm10yPct = allocationRecord.bm10yPct
    const bm30yPct = allocationRecord.bm30yPct
    const commoditiesPct = allocationRecord.commoditiesPct
    const goldPct = allocationRecord.goldPct

    if (
      !(spxTrPct instanceof Decimal) ||
      !(worldExUsEquitiesPct instanceof Decimal) ||
      !(cashPct instanceof Decimal) ||
      !(bm10yPct instanceof Decimal) ||
      !(bm30yPct instanceof Decimal) ||
      !(commoditiesPct instanceof Decimal) ||
      !(goldPct instanceof Decimal)
    ) {
      throw new Error("Allocation transform requires Decimal inputs.")
    }

    // Convert Decimal objects back to numbers for serialization
    return {
      spxTrPct: spxTrPct.toNumber(),
      worldExUsEquitiesPct: worldExUsEquitiesPct.toNumber(),
      cashPct: cashPct.toNumber(),
      bm10yPct: bm10yPct.toNumber(),
      bm30yPct: bm30yPct.toNumber(),
      commoditiesPct: commoditiesPct.toNumber(),
      goldPct: goldPct.toNumber(),
    }
  })

export const famaFrenchFactorsSchema = z.object({
  smallStocksPct: validFamaFrenchFactorLoading,
  valueStocksPct: validFamaFrenchFactorLoading,
})

const assetsSchema = z.object({
  expenseRatioPerAnnumPct: validPositiveFloat,
  allocation: allocationSchema,
  famaFrenchFactors: famaFrenchFactorsSchema,
})

const cashFlowSchema = z
  .object({
    name: z.preprocess(
      val => (val === null ? "" : val),
      z.string().min(1, "Please provide a name.")
    ),
    amount: validInteger,
    frequency: z.string(),
    inflationAdjusted: z.preprocess(
      val => (val === null ? undefined : val),
      z.boolean({
        required_error: "Please provide a value.",
      })
    ),
    start: yearMonthSchema,
    end: yearMonthSchema.optional(),
  })
  .superRefine((cashFlowInput: unknown, refinementContext: unknown) => {
    const cashFlow = cashFlowInput as CashFlowValidation
    const ctx = refinementContext as z.RefinementCtx

    if (!["annually", "monthly", "one-time"].includes(cashFlow.frequency)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Must be "annually", "monthly", or "one-time", but received "${cashFlow.frequency}" instead.`,
        path: [cashFlow.name, "frequency"],
      })
    }
    if (cashFlow.frequency === "one-time") {
      if (cashFlow.end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `An end date is not allowed for one-time cash flow ${cashFlow.name}.`,
          path: ["end"],
        })
      }
    } else if (!cashFlow.end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a valid month and year.",
        path: ["end"],
      })
    } else {
      const startValue = cashFlow.start.year * 12 + (cashFlow.start.month - 1)
      const endValue = cashFlow.end.year * 12 + (cashFlow.end.month - 1)
      if (startValue > endValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End date must equal or follow start date.",
          path: ["end"],
        })
      }
    }
  })

const baseUserParametersSchema = z
  .object({
    initialPortfolioDollars: validPositiveNonZeroInteger,
    retirementStart: yearMonthSchema,
    retirementHorizonMonths: validRetirementHorizonMonths,
    finalValuePctOfInitial: validPositiveFloat,
    initialConsumptionPerAnnumDollars: validOptionalPositiveInteger,
    inflationPerAnnumPct: validPositiveFloat,
    allocationMode: z.preprocess(
      val => val ?? "",
      z.enum(["static", "glide path"], {
        errorMap: (issueInput: unknown, contextInput: unknown) => {
          const issue = issueInput as z.ZodIssueOptionalMessage
          const ctx = contextInput as z.ErrorMapCtx

          if (issue.code === "invalid_enum_value") {
            if (ctx.data === "") {
              return {
                message: `Please select "static" or "glide path".`,
              }
            }
            return {
              message: `Must be "static" or "glide path", but received "${ctx.data}" instead.`,
            }
          }
          return { message: ctx.defaultError }
        },
      })
    ),
    glidePathLengthMonths: validPositiveInteger.optional(),
    initialAssets: assetsSchema,
    finalAssets: assetsSchema.optional(),
    cashFlows: z.array(cashFlowSchema),
  })
  .superRefine((dataInput: unknown, refinementContext: unknown) => {
    const data = dataInput as BaseUserParametersValidation
    const ctx = refinementContext as z.RefinementCtx

    const retirementStartValue = data.retirementStart.year * 12 + (data.retirementStart.month - 1)
    const retirementEndValue = retirementStartValue + data.retirementHorizonMonths
    const retirementEnd = addMonths(
      new Date(data.retirementStart.year, data.retirementStart.month - 1, 1),
      data.retirementHorizonMonths
    )
    const retirementEndStr = `${retirementEnd.getMonth() + 1}/${retirementEnd.getFullYear()}`
    for (const [index, cashFlow] of data.cashFlows.entries()) {
      const cashFlowStartValue = cashFlow.start.year * 12 + (cashFlow.start.month - 1)
      if (cashFlowStartValue < retirementStartValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Cash flow cannot start before the retirement start date.",
          path: ["cashFlows", index, "start"],
        })
      }
      // A one-time flow has no end field; the backend treats its end as its start, so the
      // start is what must not follow the retirement end.
      if (cashFlow.frequency === "one-time" && cashFlowStartValue > retirementEndValue) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Cash flow cannot occur after the retirement period, which ends ${retirementEndStr}.`,
          path: ["cashFlows", index, "start"],
        })
      }
      if (cashFlow.end) {
        const cashFlowEndValue = cashFlow.end.year * 12 + (cashFlow.end.month - 1)
        if (cashFlowEndValue > retirementEndValue) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Cash flow cannot end after the retirement period, which ends ${retirementEndStr}.`,
            path: ["cashFlows", index, "end"],
          })
        }
      }
    }

    if (data.allocationMode === "glide path" && !data.glidePathLengthMonths) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please provide a valid number.",
        path: ["glidePathLengthMonths"],
      })
    } else if (
      data.allocationMode === "glide path" &&
      data.glidePathLengthMonths != null &&
      data.glidePathLengthMonths > data.retirementHorizonMonths
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Glide path cannot be longer than retirement.",
        path: ["glidePathLengthMonths"],
      })
    } else if (data.allocationMode === "glide path" && !data.finalAssets) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Final assets are required when an asset allocation glide path is specified.",
        path: [],
      })
    } else if (data.allocationMode === "static" && data.glidePathLengthMonths) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Glide path length cannot be specified when a static asset allocation is specified.",
        path: [],
      })
    } else if (data.allocationMode === "static" && data.finalAssets) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Final assets cannot be specified when a static asset allocation is specified.",
        path: [],
      })
    }
  })

export const userParametersSchemaV1 = z.intersection(
  baseUserParametersSchema,
  z.object({
    cape: z.preprocess(
      val => val ?? "",
      z.enum(["shiller", "ern"], {
        errorMap: (issueInput: unknown, contextInput: unknown) => {
          const issue = issueInput as z.ZodIssueOptionalMessage
          const ctx = contextInput as z.ErrorMapCtx

          if (issue.code === "invalid_enum_value") {
            if (ctx.data === "") {
              return { message: `Please select "shiller" or "ern".` }
            }
            return { message: `Must be "shiller" or "ern", but received "${ctx.data}" instead.` }
          }
          return { message: ctx.defaultError }
        },
      })
    ),
  })
)

export const userParametersSchemaV2 = baseUserParametersSchema
export const currentUserParametersSchema = userParametersSchemaV2

export type UserParametersV1 = z.infer<typeof userParametersSchemaV1>
export type UserParametersV2 = z.infer<typeof currentUserParametersSchema>
export type CurrentUserParameters = UserParametersV2
export type CashFlow = z.infer<typeof cashFlowSchema>

export const initialUserParameters: CurrentUserParameters = {
  initialPortfolioDollars: 3000000,
  retirementStart: (() => {
    const today = new Date()
    return { month: today.getMonth() + 1, year: today.getFullYear() }
  })(),
  retirementHorizonMonths: 600,
  finalValuePctOfInitial: 0,
  initialConsumptionPerAnnumDollars: undefined,
  inflationPerAnnumPct: 2.5,
  allocationMode: "static",
  initialAssets: {
    expenseRatioPerAnnumPct: 0.05,
    allocation: {
      cashPct: 0,
      spxTrPct: 75,
      bm10yPct: 25,
      bm30yPct: 0,
      worldExUsEquitiesPct: 0,
      commoditiesPct: 0,
      goldPct: 0,
    },
    famaFrenchFactors: {
      smallStocksPct: 0,
      valueStocksPct: 0,
    },
  },
  cashFlows: [],
}
